import { type ReactNode, useLayoutEffect, useRef, useState } from "react"
import styled from "styled-components"

import { paginateByHeight, type PaginateItem } from "./paginateByHeight"
import { PAGE_HEIGHT, PAGE_WIDTH, pageTypography } from "./shared"

export interface PaginatorItem {
  id: string
  node: ReactNode
}

// HeaderBar height (66) + its margin-bottom (10); PageContent has no vertical padding.
const HEADER_BAR_RESERVED = 76
// Small cushion for sub-pixel rounding / margin variance between measurement and render contexts.
const SAFETY_BUFFER = 16
const PAGE_CONTENT_HORIZONTAL_PADDING = 24

export const DEFAULT_PAGE_CONTENT_WIDTH = PAGE_WIDTH - PAGE_CONTENT_HORIZONTAL_PADDING * 2
export const DEFAULT_PAGE_AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_BAR_RESERVED - SAFETY_BUFFER

// Off-screen host that mirrors the real PageContent layout context so each
// item renders with the same width and flex-column rules used for final
// display — ensuring offsetHeight reflects what will actually be drawn.
const MeasurementHost = styled.div<{ $width: number }>`
  position: absolute;
  left: -9999px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  width: ${p => p.$width}px;
  display: flex;
  flex-direction: column;
  ${pageTypography}
`

interface Props {
  items: PaginatorItem[]
  availableHeight?: number
  contentWidth?: number
  renderPage: (itemNodes: ReactNode[], pageIndex: number) => ReactNode
}

const pagesEqual = (a: string[][], b: string[][]): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].length !== b[i].length) return false
    for (let j = 0; j < a[i].length; j++) {
      if (a[i][j] !== b[i][j]) return false
    }
  }
  return true
}

export function MeasuredPaginator({
  items,
  availableHeight = DEFAULT_PAGE_AVAILABLE_HEIGHT,
  contentWidth = DEFAULT_PAGE_CONTENT_WIDTH,
  renderPage,
}: Props) {
  const measurementRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [pages, setPages] = useState<string[][] | null>(null)

  useLayoutEffect(() => {
    const measured: PaginateItem[] = items.map(item => ({
      id: item.id,
      height: measurementRefs.current[item.id]?.offsetHeight ?? 0,
    }))
    const next = paginateByHeight(measured, availableHeight)
    setPages(prev => (prev && pagesEqual(prev, next) ? prev : next))
  }, [items, availableHeight])

  const nodeById: Record<string, ReactNode> = {}
  for (const item of items) nodeById[item.id] = item.node

  return (
    <>
      <MeasurementHost $width={contentWidth} aria-hidden>
        {items.map(item => (
          <div
            key={item.id}
            ref={el => {
              measurementRefs.current[item.id] = el
            }}
          >
            {item.node}
          </div>
        ))}
      </MeasurementHost>
      {pages?.map((ids, idx) =>
        renderPage(
          ids.map(id => nodeById[id]),
          idx,
        ),
      )}
    </>
  )
}
